import Alert from '@amzn/meridian/alert';
import React, { useRef, useState } from 'react';
import { grammarJoin } from 'src/utils/helpers';
import Text from '@amzn/meridian/text';
import { TableDataRow } from 'src/utils/jmrUtils';
import Link from '@amzn/meridian/link';
import ConflictErrorBannerListItem from './conflictErrorBannerListItem';
import Column from '@amzn/meridian/column';
import infoKnockoutTokens from '@amzn/meridian-tokens/base/icon/info-knockout';
import Row from '@amzn/meridian/row';
import Icon from '@amzn/meridian/icon';
import Popover from '@amzn/meridian/popover';
import Box from '@amzn/meridian/box';

interface ConflictErrorBannerProps {
  selectedJobs: TableDataRow[];
  exceededJobIds: Set<string>;
  exceededRestrictionIds: Set<number>;
  exceededRestrictionNames: string[];
  onClickJob: (jobId: string) => void;
}

const ConflictErrorBanner = ({
  selectedJobs,
  exceededJobIds,
  exceededRestrictionIds,
  exceededRestrictionNames,
  onClickJob,
}: ConflictErrorBannerProps) => {
  const [hoursExplanationPopoverOpen, setHoursExplanationPopoverOpen] = useState(false);
  const hoursExplanationPopoverRef = useRef();

  if (!exceededRestrictionNames.length) {
    return null;
  }
  const boldedNames = exceededRestrictionNames.map((ern) => <strong>{ern}</strong>);

  const jobsExceedingRestrictions = selectedJobs.filter((j) => exceededJobIds.has(j.id));

  return (
    <Alert type="error">
      <Text>
        {/* Text element has display: flex which forces display: block onto it's children trimming out whitespace and 
            forcing a line break after our joined restrictions. We wrap the text in a div to be the flex child and allow 
            the text and <strong> elements to inline like we need them to. */}
        <div>
          The hours you are allocating exceed the associate's {grammarJoin(boldedNames)} restrictions. Modify or reduce
          allocated time for the following jobs:
        </div>
      </Text>
      <Text tag="ul">
        {/* flex direction is overriden to row somewhere (possibly Alert). 
            Use Column to get the list items displaying vertically again */}
        <Column spacing="100">
          {jobsExceedingRestrictions.map((selectedJob) => (
            <ConflictErrorBannerListItem
              key={selectedJob.id}
              {...{ selectedJob, exceededRestrictionIds, onClickJob }}
            />
          ))}
        </Column>
      </Text>
      <Box spacingInset="200" /> {/* for spacing */}
      <div
        onMouseEnter={() => setHoursExplanationPopoverOpen(true)}
        onMouseLeave={() => setHoursExplanationPopoverOpen(false)}
      >
        <Row spacing="200">
          <Link ref={hoursExplanationPopoverRef}>How are hours calculated?</Link>
          <Icon tokens={infoKnockoutTokens} />
        </Row>
      </div>
      <Popover
        position="top"
        open={hoursExplanationPopoverOpen}
        onClose={() => setHoursExplanationPopoverOpen(false)}
        anchorNode={hoursExplanationPopoverRef.current}
      >
        <Text>
          Each job has requirements for a given restriction task. This is represented by a percentage that is applied to
          the allocated shift hours of that job. When an associate is restricted to a certain number of hours in a task,
          we use this percentage to scale the restricted hours and derive the maximum amount of time that can be spent
          in the job. When more than one job is selected, we sum the total time spent in a task across the jobs to
          ensure the restriction time isn't exceeded.
        </Text>
        <Box spacingInset="200" /> {/* for spacing */}
        <Text>
          Hover over the above job lines to see the requirement percentages and resulting hours that job contributes to
          the total task hours. Click them to be taken to that job in the table.
        </Text>
      </Popover>
    </Alert>
  );
};

export default ConflictErrorBanner;
